import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  BestLapsResponse,
  PointsLeaderboardResponse,
  SessionHistoryResponse,
  CompletedSessionHistoryResponse,
  SessionResult,
  StartSessionRequest,
  EditSessionRequest,
  StartSessionResponse,
  JoinSessionResponse,
  StartStintRequest,
  Stint,
} from '@spectrace/shared';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Generic HTTP methods
  get<T>(endpoint: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}${endpoint}`);
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  post<T>(endpoint: string, body: any): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, body);
  }

  put<T>(endpoint: string, body: any): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, body);
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`);
  }

  // Auth endpoints
  getAuthStatus(): Observable<{ authenticated: boolean; member?: any }> {
    return this.http.get<{ authenticated: boolean; member?: any }>(
      `${this.baseUrl}/auth/status`,
    );
  }

  getAuthUrl(): Observable<{ authUrl: string }> {
    return this.http.get<{ authUrl: string }>(`${this.baseUrl}/auth/login`);
  }

  logout(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/auth/logout`,
      {},
    );
  }

  // Session endpoints
  startSession(request: StartSessionRequest): Observable<StartSessionResponse> {
    return this.http.post<StartSessionResponse>(
      `${this.baseUrl}/session/start`,
      request,
    );
  }

  getCurrentSession(): Observable<{ session: SessionResult | null }> {
    return this.http.get<{ session: SessionResult | null }>(
      `${this.baseUrl}/session/current`,
    );
  }

  joinSession(sessionId: string): Observable<JoinSessionResponse> {
    return this.http.post<JoinSessionResponse>(`${this.baseUrl}/session/join`, {
      sessionId,
    });
  }

  endSession(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/session/end`,
      {},
    );
  }

  editSession(
    sessionId: string,
    request: EditSessionRequest,
  ): Observable<{ session: SessionResult }> {
    return this.http.put<{ session: SessionResult }>(
      `${this.baseUrl}/session/${sessionId}`,
      request,
    );
  }

  startStint(request: StartStintRequest): Observable<{ stint: Stint }> {
    return this.http.post<{ stint: Stint }>(
      `${this.baseUrl}/session/stint/start`,
      request,
    );
  }

  endStint(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/session/stint/end`,
      {},
    );
  }

  endStintById(stintId: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/session/stint/${stintId}/end`,
      {},
    );
  }

  getStints(sessionId?: string): Observable<{ stints: Stint[] }> {
    const params: Record<string, string> = {};
    if (sessionId) {
      params['sessionId'] = sessionId;
    }
    return this.http.get<{ stints: Stint[] }>(
      `${this.baseUrl}/session/stints`,
      {
        params,
      },
    );
  }

  getSessionById(
    sessionId: string,
  ): Observable<{ session: any; activities?: unknown[] }> {
    return this.http.get<{ session: any; activities?: unknown[] }>(
      `${this.baseUrl}/session/${sessionId}`,
    );
  }

  getSessionParticipants(
    sessionId: string,
  ): Observable<{ participants: any[] }> {
    return this.http.get<{ participants: any[] }>(
      `${this.baseUrl}/session/${sessionId}/participants`,
    );
  }

  // Leaderboard endpoints
  getBestLaps(teamId?: string): Observable<BestLapsResponse> {
    const params: any = {};
    if (teamId) {
      params.teamId = teamId;
    }
    return this.http.get<BestLapsResponse>(
      `${this.baseUrl}/leaderboard/best-laps`,
      { params },
    );
  }

  getPointsLeaderboard(
    teamId?: string,
    seasonId?: string,
  ): Observable<PointsLeaderboardResponse> {
    const params: any = {};
    if (teamId) {
      params.teamId = teamId;
    }
    if (seasonId) {
      params.seasonId = seasonId;
    }
    return this.http.get<PointsLeaderboardResponse>(
      `${this.baseUrl}/leaderboard/points`,
      { params },
    );
  }

  clearLeaderboard(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/leaderboard/clear`,
      {},
    );
  }

  deleteEntry(id: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.baseUrl}/leaderboard/${id}`,
    );
  }

  deleteLap(
    entryId: string,
    lapNumber: number,
  ): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.baseUrl}/leaderboard/${entryId}/laps/${lapNumber}`,
    );
  }

  // Public browse endpoints
  getPublicSessions(): Observable<SessionHistoryResponse> {
    return this.http.get<SessionHistoryResponse>(
      `${this.baseUrl}/public/sessions`,
    );
  }

  getPublicSessionById(
    sessionId: string,
    token?: string,
  ): Observable<{
    session: SessionResult;
    stints: Stint[];
    activities?: unknown[];
  }> {
    const params: Record<string, string> = {};
    if (token) {
      params['token'] = token;
    }
    return this.http.get<{
      session: SessionResult;
      stints: Stint[];
      activities?: unknown[];
    }>(
      `${this.baseUrl}/public/sessions/${sessionId}`,
      { params },
    );
  }

  getPublicBestLaps(): Observable<BestLapsResponse> {
    return this.http.get<BestLapsResponse>(`${this.baseUrl}/public/best-laps`);
  }

  getPublicSessionOverview(
    sessionId: string,
    token?: string,
  ): Observable<any> {
    const params: Record<string, string> = {};
    if (token) {
      params['token'] = token;
    }
    return this.http.get<any>(
      `${this.baseUrl}/public/sessions/${sessionId}/overview`,
      { params },
    );
  }

  getLeaderboardSessions(): Observable<SessionHistoryResponse> {
    return this.http.get<SessionHistoryResponse>(
      `${this.baseUrl}/leaderboard/sessions`,
    );
  }

  getCompletedSessionHistory(params?: {
    teamId?: string;
    limit?: number;
    offset?: number;
    cursor?: string;
  }): Observable<CompletedSessionHistoryResponse> {
    const queryParams: Record<string, string> = {};
    if (params?.teamId) {
      queryParams['teamId'] = params.teamId;
    }
    if (params?.limit !== undefined) {
      queryParams['limit'] = String(params.limit);
    }
    if (params?.offset !== undefined) {
      queryParams['offset'] = String(params.offset);
    }
    if (params?.cursor) {
      queryParams['cursor'] = params.cursor;
    }

    return this.http.get<CompletedSessionHistoryResponse>(
      `${this.baseUrl}/session/history`,
      { params: queryParams },
    );
  }

  getLeaderboardSessionById(
    sessionId: string,
  ): Observable<{
    session: SessionResult;
    stints: Stint[];
    activities?: unknown[];
  }> {
    return this.http.get<{
      session: SessionResult;
      stints: Stint[];
      activities?: unknown[];
    }>(
      `${this.baseUrl}/leaderboard/sessions/${sessionId}`,
    );
  }

  getLeaderboardSessionOverview(sessionId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/leaderboard/sessions/${sessionId}/overview`,
    );
  }

  createSessionShareLink(
    sessionId: string,
    visibility: 'public' | 'private',
    expiresInHours?: number,
  ): Observable<{
    visibility: 'public' | 'private';
    url: string;
    previewUrl: string;
    expiresAt: string | null;
    token: string | null;
  }> {
    return this.http.post<{
      visibility: 'public' | 'private';
      url: string;
      previewUrl: string;
      expiresAt: string | null;
      token: string | null;
    }>(`${this.baseUrl}/leaderboard/sessions/${sessionId}/share`, {
      visibility,
      expiresInHours,
    });
  }

  downloadLeaderboardSessionCsv(sessionId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/leaderboard/sessions/${sessionId}/export.csv`, {
      responseType: 'blob',
    });
  }

  downloadLeaderboardSessionShareCard(sessionId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/leaderboard/sessions/${sessionId}/share-card.png`,
      {
        responseType: 'blob',
      },
    );
  }

  getPublicSessionCsvUrl(sessionId: string, token?: string): string {
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${this.baseUrl}/public/sessions/${sessionId}/export.csv${tokenQuery}`;
  }

  // Session catalog endpoints (authenticated users)
  getTracks(): Observable<{ tracks: any[]; count: number }> {
    return this.http.get<{ tracks: any[]; count: number }>(
      `${this.baseUrl}/session/tracks`,
    );
  }

  getCarClasses(): Observable<{ carClasses: any[]; count: number }> {
    return this.http.get<{ carClasses: any[]; count: number }>(
      `${this.baseUrl}/session/car-classes`,
    );
  }

  getCars(): Observable<{ cars: any[]; count: number }> {
    return this.http.get<{ cars: any[]; count: number }>(
      `${this.baseUrl}/session/cars`,
    );
  }

  syncIRacingData(): Observable<{
    success: boolean;
    message: string;
    data: any;
  }> {
    return this.http.post<{ success: boolean; message: string; data: any }>(
      `${this.baseUrl}/admin/sync-iracing-data`,
      {},
    );
  }

  // iRacing endpoints
  generateIRacingLaunchUrl(
    trackId: string,
    carId: string,
    iracingEmail: string,
    iracingPassword: string,
    iracingCustomerId?: string,
  ): Observable<{ launchUrl: string }> {
    return this.http.post<{ launchUrl: string }>(
      `${this.baseUrl}/iracing/generate-launch-url`,
      {
        trackId,
        carId,
        iracingEmail,
        iracingPassword,
        iracingCustomerId,
      },
    );
  }
}
